import {
  collection,
  doc,
  query,
  serverTimestamp,
  type Timestamp,
  where,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { buildUnsupportedConverter } from '../shared/noops'
import { type FirestorePromptRubricAIEvent, schema } from './schema'
import { type FirebaseRepository } from '../../models/FirebaseRepository'
import {
  collectionSnapshots,
  modelListStream,
} from '../../firestore-mobx/stream'
import {
  addDocWithError,
  deleteDocWithError,
  getDocWithError,
} from '../../firestore-mobx/fetch'
import { PromptType } from '../PromptVersion/types'
import { type RubricType } from '../PromptRubricType/types'
import { PromptRubricAIEvent } from '../../models/PromptRubricAIEvent'

export * from './schema'

const unsupported = buildUnsupportedConverter<FirestorePromptRubricAIEvent>()

const readConverter: FirestoreDataConverter<FirestorePromptRubricAIEvent> = {
  toFirestore: unsupported.toFirestore,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

const getColRef = (repository: FirebaseRepository) =>
  collection(
    repository.firestore,
    'prompt',
    PromptType.rubric,
    'ai'
  ).withConverter(readConverter)

// todo: add compound index for rubricType and promptVersionId on this collection
export const getPromptRubricAIEvents = (
  repository: FirebaseRepository,
  {
    rubricType,
    promptVersionId,
  }: {
    rubricType: RubricType
    promptVersionId: string
  }
) => {
  const q = query(
    getColRef(repository),
    where('promptType', '==', rubricType.toString()),
    where('promptVersionId', '==', promptVersionId)
  )
  return modelListStream(repository, q, PromptRubricAIEvent)
}

export const startRubricPromptTestForRoom = async (
  repository: FirebaseRepository,
  {
    rubricType,
    promptVersionId,
    roomId,
    startSlideId,
    slideId,
    rubricPrompt,
  }: {
    rubricType: RubricType
    promptVersionId: string
    roomId: string
    startSlideId: string
    slideId: string
    rubricPrompt: string
  }
) => {
  const colRef = getColRef(repository)

  await addDocWithError(
    colRef.withConverter(null),
    {
      endedAt: null,
      promptType: rubricType.toString(),
      promptVersionId,
      roomId,
      startedAt: serverTimestamp(),
      startSlideId: startSlideId,
      slideId,
      rubricPrompt,
    },
    'StartRubricPromptTestForRoom'
  )
}

export const deletePromptRubricTest = async (
  repository: FirebaseRepository,
  {
    aiId,
  }: {
    aiId: string
  }
) => {
  const colRef = getColRef(repository)
  const docRef = doc(colRef, aiId)

  await deleteDocWithError(docRef, 'DeletePromptRubricTestError')
}

export const getResultsForRubricPromptTests = (
  repository: FirebaseRepository,
  {
    rubricType,
    promptVersionId,
  }: {
    rubricType: RubricType
    promptVersionId: string
  }
) => {
  type Result = QueryDocumentSnapshot<{
    updatedAt: Timestamp
    roomId: string
    aIId: string
    assignmentId?: string
    sectionId?: string
    aiResultsType: RubricType
    promptVersionId: string
  }>

  const resultsQuery = collection(
    repository.firestore,
    'prompt',
    PromptType.rubric,
    'type',
    rubricType.toString(),
    'prompt_version',
    promptVersionId,
    'test'
  )

  return collectionSnapshots(resultsQuery).map(({ docs }) => docs as Result[])
}

export const fetchResultsForSuccessfulRubricPromptTest = async (
  repository: FirebaseRepository,
  {
    rubricType,
    promptVersionId,
    testId,
  }: {
    rubricType: RubricType
    promptVersionId: string
    testId: string
  }
) => {
  const resultsRef = doc(
    repository.firestore,
    'prompt',
    PromptType.rubric,
    'type',
    rubricType.toString(),
    'prompt_version',
    promptVersionId,
    'test',
    testId
  )
  const snap = await getDocWithError(
    resultsRef,
    'fetchResultsForSuccessfulRubricPromptTest'
  )
  if (!snap.exists())
    throw new Error(
      `No results found for rubric type ${rubricType}, version ${promptVersionId}, test ${testId}`
    )
  return snap.data()
}
